//#region Imports

// @angular
import {Routes} from '@angular/router';
import {RouteName, UserRole} from '@ew/enums';

// @ew
import {authRedirectionGuard} from '@ew/auth-error-guard';
import {authGuard} from '@ew/auth-guard';
import {createProfileGuard} from '@ew/create-profile-guard';
import {GlobalUserState} from '@ew/global-user';
import {checkTutorialGuard} from '@ew/tutorial-guard';

//#endregion

/**
 * Role-Based Component Loader
 *
 * Dynamically loads different component implementations based on the current user's role.
 * This enables the mobile app to display role-specific UI and functionality while
 * maintaining a unified routing structure.
 *
 * Use Cases:
 * - Company users see business management dashboards and analytics
 * - Customer users see booking history and wait time tracking
 * - Same route path can render completely different components
 *
 * Mobile Navigation Pattern:
 * This pattern is essential for the mobile app because:
 * - Single codebase supports both company and customer users
 * - Navigation tabs remain consistent across roles
 * - Each role gets optimized mobile UX for their specific needs
 * - Reduces app complexity by sharing route paths
 *
 * @param companyPathLoad - Lazy-loaded component for company/business users
 * @param customerPathLoad - Lazy-loaded component for customer users
 * @returns The appropriate component based on user role
 *
 * @example
 * // Dashboard route loads different components based on role:
 * loadPathByRole(
 *   import('company-dashboard'),  // Business analytics, queue management
 *   import('customer-dashboard')  // Personal bookings, tracked services
 * )
 */
const loadPathByRole = (companyPathLoad: any, customerPathLoad: any) => {
  if (GlobalUserState.userRole === UserRole.COMPANY) {
    return companyPathLoad;
  } else if (GlobalUserState.userRole === UserRole.CUSTOMER) {
    return customerPathLoad;
  }
};

/**
 * Mobile App Shell Child Routes Configuration
 *
 * Defines the main navigation structure within the authenticated mobile app shell.
 * These routes are children of the app shell component and are displayed within
 * the bottom tab navigation pattern.
 *
 * Mobile Navigation Architecture:
 * - Uses Ionic tabs for bottom navigation (Dashboard, Explore, Bookings, etc.)
 * - All routes lazy-loaded to optimize mobile app bundle size
 * - Role-based routing to show different views for company vs customer users
 * - Deep linking support for push notifications and external links
 *
 * Key Mobile Features:
 * - QR code scanning for service tracking
 * - Real-time chat with businesses
 * - Activity feed with notification badges
 * - Calendar-based appointment booking
 * - Map-based business exploration
 *
 * Route Organization:
 * 1. Core tabs (Dashboard, Explore, Bookings, Settings)
 * 2. Feature routes (Chat, Track, Reviews, Analytics)
 * 3. Detail routes (Service details, Activity details, Chat rooms)
 * 4. Modal/overlay routes (Reservation flow, Profile creation)
 *
 * @see loadPathByRole - Used throughout for role-based component loading
 * @see appMobileShellRoutes - Parent routes with guards
 */
export const appMobileShellChildrenRoutes: Routes = [
  // Root redirect - Default landing page for authenticated users
  // Redirects to Explore tab to encourage discovery of services
  {
    path: RouteName.Empty,
    redirectTo: RouteName.Explore,
    pathMatch: 'full',
  },

  // Dashboard Tab - Main home screen with role-specific content
  // Company: Business analytics, queue management, staff overview
  // Customer: Active bookings, tracked services, quick actions
  {
    path: RouteName.Dashboard,
    loadComponent: async () =>
      loadPathByRole(
        (await import('@ew-mobile/mobile-dashboard-overview')).MobileDashboardOverviewComponent,
        (await import('@ew-mobile/mobile-customer-dashboard-overview'))
          .MobileCustomerDashboardOverviewComponent,
      ),
    loadChildren: async () => (await import('@ew-mobile/mobile-dashboard-core')).mobileRoutes,
  },
  // Service Item Details - Detailed view of a specific service in the queue
  // Shows wait time, position, service info, and communication options
  {
    path: `${RouteName.ItemDetails}/:serviceItemId`,
    loadComponent: async () =>
      (await import('@ew-mobile/service-item-details')).ServiceItemDetailsComponent,
  },

  // Explore Tab - Discover and search for businesses and services
  // Map view, list view, filters, categories, and search functionality
  {
    path: RouteName.Explore,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-explore-overview')).MobileExploreOverviewComponent,
    loadChildren: async () =>
      (await import('@ew-mobile/mobile-explore-core')).exploreFeaturesRoutes,
  },

  // Activity Feed Tab - Notification center with role-specific updates
  // Company: New bookings, cancellations, queue updates, customer messages
  // Customer: Booking confirmations, position updates, service ready notifications
  {
    path: RouteName.ActivityFeed,
    loadComponent: async () =>
      loadPathByRole(
        (await import('@ew-mobile/mobile-activity-feed-overview'))
          .MobileActivityFeedOverviewComponent,
        (await import('@ew-mobile/mobile-activity-feed-customer-overview'))
          .MobileActivityFeedCustomerOverviewComponent,
      ),
  },

  // Chat - Real-time messaging conversation list
  // Lists all active conversations with customers or businesses
  {
    path: RouteName.Chat,
    loadComponent: async () =>
      loadPathByRole(
        (await import('@ew-mobile/conversation-list')).ConversationListComponent,
        (await import('@ew-mobile/conversation-list')).ConversationListComponent,
      ),
  },

  // Chat — New thread picker (service item selector)
  // Companies pick a service item to start a fresh conversation thread.
  {
    path: `${RouteName.Chat}/new`,
    loadComponent: async () =>
      (await import('@ew-mobile/conversation-list')).NewChatPickerComponent,
  },

  // Chat Room - Individual conversation with messages
  // Real-time messaging with WebSocket updates, typing indicators, read receipts
  {
    path: `${RouteName.Chat}/:serviceItemId`,
    loadComponent: async () =>
      loadPathByRole(
        (await import('@ew-mobile/chat-messages')).ChatMessagesComponent,
        (await import('@ew-mobile/chat-messages')).ChatMessagesComponent,
      ),
  },
  // Activity Feed Details - Detailed view of a single notification/activity
  // Shows full context, related actions, and navigation to relevant screens
  {
    path: `${RouteName.ActivityFeed}/:activityFeedId`,
    loadComponent: async () =>
      loadPathByRole(
        (await import('@ew-mobile/mobile-activity-feed-details'))
          .MobileActivityFeedDetailsComponent,
        (await import('@ew-mobile/mobile-activity-feed-customer-details'))
          .MobileActivityFeedCustomerDetailsComponent,
      ),
  },

  // Track - QR Code Scanner for service tracking
  // Uses Capacitor camera plugin to scan QR codes and track services
  // Mobile-only feature leveraging device camera
  {
    path: RouteName.Track,
    loadComponent: async () =>
      (await import('@ew-mobile/scan-tracking-code')).ScanTrackingCodeComponent,
  },

  // Track Details - View tracked service status by tracking code
  // Shows real-time wait time, queue position, estimated completion
  // Updates via WebSocket for live status changes
  {
    path: `${RouteName.Track}/:trackingCode`,
    loadComponent: async () =>
      (await import('@ew-mobile/service-item-track-details-mobile'))
        .ServiceItemTrackDetailsMobileComponent,
  },
  // Track Calendar - Calendar view for tracked service appointments
  // Mobile-optimized calendar with touch gestures and date selection
  {
    path: `${RouteName.Track}/:companyId/${RouteName.Calendar}`,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-track-calendar')).MobileTrackCalendarComponent,
  },

  // Analytics - Business performance metrics and charts
  // Company: Revenue, bookings, wait times, customer satisfaction
  // Customer: Personal usage statistics, time saved, favorite businesses
  {
    path: RouteName.Analytics,
    loadComponent: async () =>
      loadPathByRole(
        (await import('@ew-mobile/mobile-analytic-overview')).MobileAnalyticOverviewComponent,
        (await import('@ew-mobile/mobile-customer-analytic-overview')).MobileCustomerAnalyticOverviewComponent,
      ),
  },

  // Bookings Tab - List of all user bookings/reservations
  // Active, upcoming, and past bookings with filtering and search
  {
    path: RouteName.Bookings,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-booking-overview')).MobileBookingOverviewComponent,
  },

  // Booking Details - Detail view for a single booking by id
  {
    path: `${RouteName.Bookings}/:bookingId`,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-booking-overview')).MobileBookingDetailsComponent,
  },

  // Reservation Flow - Multi-step booking process
  // Mobile-optimized stepper for: select service → choose time → add details → confirm
  // Uses Ionic slides/stepper for smooth mobile UX
  {
    path: `${RouteName.Reserve}/:companyId`,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-reservation-stepper')).MobileReservationStepperComponent,
  },

  // Reservation Confirmation - Success screen after booking
  // Shows booking details, QR code, and next steps
  {
    path: RouteName.ConfirmReservation,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-confirm-reservation')).MobileConfirmReservationComponent,
  },
  // Company Reviews - Customer reviews and ratings for a business
  // List view with rating stars, comments, and response options
  {
    path: `${RouteName.Company}/:companyId/${RouteName.Reviews}`,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-reviews')).MobileReviewsComponent,
  },

  // Settings Tab - App preferences, profile, and account management
  // Theme, notifications, language, privacy, logout
  // Nested routes for different settings sections
  {
    path: RouteName.Settings,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-settings-core')).SettingsRouterOutletComponent,
    loadChildren: async () => (await import('@ew-mobile/mobile-settings-core')).settingsRoutes,
  },

  // Pricing Plans - Subscription plans for company users
  // View available plans, upgrade/downgrade, manage subscription
  {
    path: RouteName.Plans,
    loadComponent: async () =>
      (await import('@ew-mobile/mobile-pricing-plans')).MobilePricingPlansComponent,
  },

  // Payment Success - Confirmation screen after successful subscription payment
  // Redirected here from Stripe checkout success page
  {
    path: RouteName.PaymentSuccess,
    loadComponent: async () =>
      (await import('@ew-web/payment-success')).PaymentSuccessComponent,
  },
];

/**
 * Mobile App Shell Parent Routes
 *
 * Top-level routes that define the main app structure and authentication flow.
 * These routes wrap the child routes and provide the navigation shell.
 *
 * Route Guards:
 * - authRedirectionGuard: Redirects unauthenticated users to login
 * - authGuard: Ensures user is authenticated before accessing shell
 * - createProfileGuard: Redirects incomplete profiles to profile creation
 * - checkTutorialGuard: Shows tutorial for first-time users
 *
 * Mobile App Flow:
 * 1. User opens app
 * 2. Auth guard checks authentication status
 * 3. If not authenticated → redirect to login (handled by auth routes)
 * 4. If authenticated but incomplete profile → profile creation screen
 * 5. If first-time user → tutorial slides
 * 6. Otherwise → load appropriate shell (company or customer) with tab navigation
 *
 * Shell Components:
 * - AppMobileShellComponent: Company/business owner interface with bottom tabs
 * - CustomerMobileShellComponent: Customer interface with bottom tabs
 * - Both shells provide: Dashboard, Explore, Bookings, Activities, Settings tabs
 *
 * @see appMobileShellChildrenRoutes - Child routes displayed within the shell
 */
export const appMobileShellRoutes: Routes = [
  // Main App Shell - Role-based layout with bottom tab navigation
  // Protected by authentication guards
  {
    path: RouteName.Empty,
    loadComponent: async () =>
      loadPathByRole(
        (await import('@ew-mobile/app-mobile-shell')).AppMobileShellComponent,
        (await import('@ew-mobile/customer-mobile-shell')).CustomerMobileShellComponent,
      ),
    children: [...appMobileShellChildrenRoutes],
    canActivate: [authRedirectionGuard, authGuard],
  },

  // Profile Creation - Required step for new users
  // Shown after registration to complete user profile
  // Guard ensures only incomplete profiles see this screen
  {
    path: RouteName.CreateProfile,
    loadComponent: async () =>
      (await import('@ew-mobile/create-profile-mobile')).CreateProfileMobileComponent,
    canActivate: [createProfileGuard],
  },

  // Tutorial - First-time user onboarding
  // Swipeable slides explaining app features
  // Only shown once per user (tracked in local storage)
  {
    path: RouteName.Tutorial,
    loadComponent: async () => (await import('@ew-mobile/tutorial')).TutorialComponent,
    canMatch: [checkTutorialGuard],
  },
];
